import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, FlaskConical, Calendar, Package, User, Plus, 
  Trash2, CheckCircle, AlertCircle, XCircle, Clock, Edit, MoreVertical, FileText, ExternalLink,
  Image as ImageIcon, Upload, Download, X, Users, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { cs } from 'date-fns/locale';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: cs });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd.MM.yyyy HH:mm', { locale: cs });
  } catch {
    return dateStr;
  }
};

const RatingBadge = ({ rating }) => {
  const styles = {
    A: 'bg-green-100 text-green-700 border-green-200',
    B: 'bg-amber-100 text-amber-700 border-amber-200',
    C: 'bg-red-100 text-red-700 border-red-200'
  };
  const labels = {
    A: 'Zcela vyhovující',
    B: 'S výhradami',
    C: 'Nevyhovující'
  };
  
  return (
    <Badge className={`${styles[rating] || 'bg-slate-100'} border font-semibold`}>
      {rating} - {labels[rating] || rating}
    </Badge>
  );
};

const RatingButton = ({ value, selected, onClick, label }) => {
  const styles = {
    A: selected ? 'bg-green-500 text-white border-green-500' : 'bg-white text-green-700 border-green-300 hover:bg-green-50',
    B: selected ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50',
    C: selected ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-700 border-red-300 hover:bg-red-50'
  };
  
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg border-2 font-semibold text-sm transition-all ${styles[value]}`}
    >
      {value}
    </button>
  );
};

// Evaluation card component for displaying individual evaluations
const EvaluationCard = ({ evaluation, onDelete, groupBadge }) => {
  const ratingsValues = Object.values(evaluation.ratings || {});
  const hasC = ratingsValues.includes('C');
  const hasB = ratingsValues.includes('B');
  const allA = ratingsValues.length > 0 && ratingsValues.every(r => r === 'A');
  
  let cardBgClass = 'bg-white border-slate-200 hover:border-slate-300';
  let innerBgClass = 'bg-slate-50';
  let noteBgClass = 'bg-slate-50 text-slate-600';
  let statusBadge = null;
  
  if (hasC) {
    cardBgClass = 'bg-red-50 border-red-300 hover:border-red-400';
    innerBgClass = 'bg-red-100/50';
    noteBgClass = 'bg-red-100 text-red-800';
    statusBadge = <Badge className="bg-red-500 text-white text-xs">Nevyhovující</Badge>;
  } else if (hasB) {
    cardBgClass = 'bg-amber-50 border-amber-300 hover:border-amber-400';
    innerBgClass = 'bg-amber-100/50';
    noteBgClass = 'bg-amber-100 text-amber-800';
    statusBadge = <Badge className="bg-amber-500 text-white text-xs">S výhradami</Badge>;
  } else if (allA) {
    cardBgClass = 'bg-green-50 border-green-300 hover:border-green-400';
    innerBgClass = 'bg-green-100/50';
    noteBgClass = 'bg-green-100 text-green-800';
    statusBadge = <Badge className="bg-green-500 text-white text-xs">Vyhovující</Badge>;
  }
  
  return (
    <div className={`border rounded-lg p-4 transition-colors ${cardBgClass}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <User className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-900">{evaluation.evaluator_name}</span>
          <span className="text-xs text-slate-500">
            {formatDateTime(evaluation.created_at)}
          </span>
          {groupBadge}
          {statusBadge}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(evaluation.id)}
          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        {Object.entries(evaluation.ratings || {}).map(([field, rating]) => (
          <div key={field} className={`flex items-center justify-between rounded-lg px-3 py-2 ${innerBgClass}`}>
            <span className="text-xs text-slate-600">{field}</span>
            <span className={`font-bold text-sm ${
              rating === 'A' ? 'text-green-600' : 
              rating === 'B' ? 'text-amber-600' : 'text-red-600'
            }`}>
              {rating}
            </span>
          </div>
        ))}
      </div>
      
      {evaluation.note && (
        <div className={`rounded-lg p-3 text-sm ${noteBgClass}`}>
          <span className="font-medium">Poznámka: </span>
          {evaluation.note}
        </div>
      )}
    </div>
  );
};

export const SensorikaDetailPage = ({ sensoryType: propSensoryType }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [test, setTest] = useState(null);
  const [evalFields, setEvalFields] = useState([]);
  const [allEvalFields, setAllEvalFields] = useState([]);
  const [testFields, setTestFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Get sensory type from test data or prop
  const sensoryType = test?.sensory_type || propSensoryType || 'hotovky';
  const basePath = `/senzorika/${sensoryType}`;
  
  // New evaluation form
  const [ratings, setRatings] = useState({});
  const [note, setNote] = useState('');
  
  // Team member selection
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSelectOpen, setMemberSelectOpen] = useState(false);
  
  // Edit form
  const [editData, setEditData] = useState({});
  
  // Linked reports (created from this sensory test)
  const [linkedReports, setLinkedReports] = useState([]);
  
  // Photo gallery
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Scheduled team for this test date
  const [scheduledTeam, setScheduledTeam] = useState([]);
  
  // Check if user is admin
  const isAdmin = user?.permissions?.includes('admin') || user?.role === 'admin';

  // Compute visible eval fields for the evaluation form based on selected member
  const visibleEvalFieldsForForm = React.useMemo(() => {
    if (sensoryType !== 'krabicky' || !selectedMember) {
      return allEvalFields.length > 0 ? allEvalFields : evalFields;
    }
    const isGroupA = selectedMember.is_group_a || false;
    return isGroupA 
      ? allEvalFields 
      : allEvalFields.filter(f => !f.is_group_a_only);
  }, [allEvalFields, evalFields, selectedMember, sensoryType]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      // First get the test to know its sensory_type
      const testRes = await api.get(`/sensory/tests/${id}`);
      const testSensoryType = testRes.data.sensory_type || 'hotovky';
      
      const [fieldsRes, testFieldsRes, reportsRes, teamRes] = await Promise.all([
        api.get('/sensory/eval-fields', { params: { sensory_type: testSensoryType } }),
        api.get('/sensory/test-fields', { params: { sensory_type: testSensoryType } }),
        api.get(`/reports?source_sensory_test_id=${id}`),
        api.get('/sensory-team', { params: { sensory_type: testSensoryType } })
      ]);
      setTest(testRes.data);
      
      // Filter eval fields based on team member's group A membership (for Krabicky)
      // Check if current user is in team as Group A member
      const allActiveFields = fieldsRes.data.filter(f => f.is_active);
      setAllEvalFields(allActiveFields); // Store all fields for later use
      
      let isUserGroupA = false;
      
      if (testSensoryType === 'krabicky' && user) {
        // Check if user is in sensory team for krabicky as Group A member
        const teamMember = teamRes.data.find(m => 
          (m.email && m.email.toLowerCase() === user.email?.toLowerCase()) ||
          (`${m.first_name} ${m.last_name}`.toLowerCase() === user.name?.toLowerCase())
        );
        isUserGroupA = teamMember?.is_group_a || false;
      }
      
      const visibleFields = testSensoryType === 'krabicky' && !isUserGroupA
        ? allActiveFields.filter(f => !f.is_group_a_only)
        : allActiveFields;
      
      setEvalFields(visibleFields);
      setTestFields(testFieldsRes.data.filter(f => f.is_active));
      setLinkedReports(reportsRes.data || []);
      setTeamMembers(teamRes.data || []);
      
      // Fetch scheduled team for this test date
      if (testRes.data.test_date) {
        try {
          const scheduleRes = await api.get(`/sensory-team-schedule/by-date/${testRes.data.test_date}`, {
            params: { sensory_type: testSensoryType }
          });
          setScheduledTeam(scheduleRes.data.members || []);
        } catch {
          setScheduledTeam([]);
        }
      }
    } catch (error) {
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  };

  // Photo upload handler
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of acceptedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/sensory/tests/${id}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${acceptedFiles.length} foto nahráno`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při nahrávání');
    } finally {
      setUploading(false);
    }
  }, [id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const handleDeletePhoto = async (fileId) => {
    if (!window.confirm('Opravdu chcete smazat toto foto?')) return;
    
    try {
      await api.delete(`/sensory/tests/${id}/photos/${fileId}`);
      toast.success('Foto smazáno');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
  };

  const handleDownloadPhoto = (attachment) => {
    const link = document.createElement('a');
    link.href = `data:${attachment.content_type};base64,${attachment.data}`;
    link.download = attachment.filename;
    link.click();
  };

  const openLightbox = (attachment) => {
    setLightboxImage(attachment);
    setLightboxOpen(true);
  };
  
  // Open member selection dialog
  const handleOpenEvaluation = () => {
    if (teamMembers.length === 0) {
      toast.error('Nejsou definováni žádní členové senzorického týmu. Přidejte je v Administraci.');
      return;
    }
    setMemberSelectOpen(true);
  };
  
  // Select member and open evaluation form
  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setMemberSelectOpen(false);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!window.confirm('Opravdu chcete smazat tuto zkoušku? Tato akce je nevratná.')) return;
    
    try {
      await api.delete(`/sensory/tests/${id}`);
      toast.success('Zkouška smazána');
      navigate(basePath);
    } catch (error) {
      toast.error('Chyba při mazání zkoušky');
    }
  };

  const openEditDialog = () => {
    setEditData({
      test_date: test.test_date || '',
      food_name: test.food_name || '',
      batch: test.batch || '',
      production_date: test.production_date || '',
      dmt: test.dmt || '',
      custom_fields: test.custom_fields || {}
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/sensory/tests/${id}`, editData);
      toast.success('Zkouška aktualizována');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Chyba při ukládání');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddEvaluation = async () => {
    // Validate all visible fields have ratings
    for (const field of visibleEvalFieldsForForm) {
      if (!ratings[field.name]) {
        toast.error(`Vyplňte hodnocení pro "${field.name}"`);
        return;
      }
    }
    
    // If rating B or C, note should be filled
    const hasBorC = Object.values(ratings).includes('B') || Object.values(ratings).includes('C');
    if (hasBorC && !note.trim()) {
      toast.error('Při hodnocení B nebo C je nutné vyplnit poznámku');
      return;
    }

    if (!selectedMember) {
      toast.error('Nebyl vybrán člen týmu');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/sensory/tests/${id}/evaluations`, { 
        ratings, 
        note,
        evaluator_id: selectedMember.id,
        evaluator_name: `${selectedMember.first_name} ${selectedMember.last_name}`
      });
      toast.success('Hodnocení přidáno');
      setDialogOpen(false);
      setRatings({});
      setNote('');
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      toast.error('Chyba při ukládání hodnocení');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvaluation = async (evalId) => {
    if (!window.confirm('Opravdu chcete smazat toto hodnocení?')) return;
    
    try {
      await api.delete(`/sensory/evaluations/${evalId}`);
      toast.success('Hodnocení smazáno');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání hodnocení');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/sensory/tests/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Check if response is actually an error (JSON) instead of PDF
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Neznámá chyba');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `senzorika_${test.food_name?.replace(/\s+/g, '_') || 'zkouska'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportováno');
    } catch (error) {
      console.error('PDF export error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Neznámá chyba';
      toast.error(`Chyba při exportu PDF: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!test) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Zkouška nenalezena</p>
          <Button onClick={() => navigate(basePath)} className="mt-4">
            Zpět na seznam
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 pt-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{test.food_name}</h1>
              <p className="text-slate-500">Senzorická zkouška</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* PDF Export button */}
            <Button 
              variant="outline"
              onClick={handleExportPDF}
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
              data-testid="export-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            
            {/* Create report from sensory test */}
            <Button 
              variant="outline"
              onClick={() => navigate(`/reports/new?from_sensory=${id}`)}
              className="border-teal-200 text-teal-700 hover:bg-teal-50"
            >
              <FileText className="h-4 w-4 mr-2" />
              Vytvořit hlášení
            </Button>
            
            {/* Admin actions */}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={openEditDialog}>
                    <Edit className="h-4 w-4 mr-2" />
                    Upravit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Smazat
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Add evaluation button */}
            <Button 
              onClick={handleOpenEvaluation}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Plus className="h-4 w-4 mr-2" />
              Přidat hodnocení
            </Button>

            {/* Member selection dialog */}
            <Dialog open={memberSelectOpen} onOpenChange={setMemberSelectOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Vyberte hodnotitele</DialogTitle>
                  <DialogDescription>
                    Zvolte člena senzorického týmu, který provádí hodnocení
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-2 max-h-[400px] overflow-y-auto">
                  {teamMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => handleSelectMember(member)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50 transition-all text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                        {member.first_name[0]}{member.last_name[0]}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">
                          {member.first_name} {member.last_name}
                        </div>
                        <div className="text-sm text-slate-500">{member.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>

            {/* Evaluation form dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setSelectedMember(null);
                setRatings({});
                setNote('');
              }
            }}>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nové hodnocení</DialogTitle>
                  <DialogDescription>
                    <div className="space-y-2 mt-2">
                      {/* Food name */}
                      <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg border border-purple-100">
                        <FlaskConical className="h-4 w-4 text-purple-500" />
                        <span className="font-medium text-purple-800">{test.food_name}</span>
                        {test.batch && (
                          <span className="text-purple-600 text-sm">• Šarže: {test.batch}</span>
                        )}
                      </div>
                      {/* Evaluator */}
                      {selectedMember && (
                        <span className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs font-semibold">
                            {selectedMember.first_name[0]}{selectedMember.last_name[0]}
                          </span>
                          <span className="font-medium text-slate-700">
                            {selectedMember.first_name} {selectedMember.last_name}
                          </span>
                        </span>
                      )}
                    </div>
                  </DialogDescription>
                </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Rating explanation */}
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="font-medium text-slate-700 mb-1">Stupnice hodnocení:</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
                    <span><span className="font-semibold text-green-600">A</span> - Zcela vyhovující</span>
                    <span><span className="font-semibold text-amber-600">B</span> - S výhradami</span>
                    <span><span className="font-semibold text-red-600">C</span> - Nevyhovující</span>
                  </div>
                </div>

                {/* Quick fill button - all A */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
                  onClick={() => {
                    const allA = {};
                    visibleEvalFieldsForForm.forEach(field => { allA[field.name] = 'A'; });
                    setRatings(allA);
                  }}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Zcela vyhovující (všechny A)
                </Button>
                
                {/* Rating fields - compact grid */}
                <div className="grid gap-3">
                  {visibleEvalFieldsForForm.map((field) => (
                    <div key={field.id} className="flex items-center justify-between gap-4 p-2 bg-slate-50 rounded-lg">
                      <Label className="font-medium text-slate-700 min-w-[100px]">{field.name}</Label>
                      <div className="flex gap-1">
                        {['A', 'B', 'C'].map((value) => (
                          <RatingButton
                            key={value}
                            value={value}
                            selected={ratings[field.name] === value}
                            onClick={() => setRatings(prev => ({ ...prev, [field.name]: value }))}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Note */}
                <div className="space-y-2">
                  <Label>
                    Poznámka
                    {(Object.values(ratings).includes('B') || Object.values(ratings).includes('C')) && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Doplňující poznámky k hodnocení..."
                    rows={2}
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Zrušit
                  </Button>
                  <Button 
                    onClick={handleAddEvaluation}
                    disabled={submitting}
                    className="bg-gradient-to-r from-purple-500 to-pink-500"
                  >
                    {submitting ? 'Ukládám...' : 'Uložit hodnocení'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column - Test info & Summary */}
          <div className="space-y-6">
            {/* Test Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                  <FlaskConical className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="font-semibold text-slate-900">Informace o zkoušce</h2>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Den zkoušky:</span>
                  <span className="font-medium">{test.test_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Název jídla:</span>
                  <span className="font-medium">{test.food_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Šarže:</span>
                  <span className="font-medium">{test.batch}</span>
                </div>
                {test.production_date && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Datum výroby:</span>
                    <span className="font-medium">{test.production_date}</span>
                  </div>
                )}
                {test.dmt && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">DMT:</span>
                    <span className="font-medium">{test.dmt}</span>
                  </div>
                )}
                {/* Custom fields */}
                {test.custom_fields && Object.entries(test.custom_fields).map(([key, value]) => (
                  value && (
                    <div key={key} className="flex justify-between">
                      <span className="text-slate-500">{key}:</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  )
                ))}
                <div className="pt-2 border-t border-slate-100">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Vytvořil:</span>
                    <span className="font-medium">{test.created_by_name}</span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Vytvořeno:</span>
                  <span className="font-medium">{formatDateTime(test.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Scheduled Team */}
            {scheduledTeam.length > 0 && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
                <h2 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Plánovaní hodnotitelé
                </h2>
                <p className="text-sm text-amber-700 mb-3">
                  Pro den {formatDate(test.test_date)} jsou naplánováni:
                </p>
                <div className="flex flex-wrap gap-2">
                  {scheduledTeam.map((member) => {
                    // Check if this member has already submitted an evaluation
                    const hasEvaluated = test.evaluations?.some(
                      (e) => e.evaluator_id === member.id
                    );
                    
                    return (
                      <span
                        key={member.id}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          hasEvaluated 
                            ? 'bg-green-100 border border-green-300 text-green-800' 
                            : 'bg-white/80 border border-amber-200 text-amber-800'
                        }`}
                        title={hasEvaluated ? 'Již ohodnotil/a' : 'Čeká na hodnocení'}
                      >
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-semibold ${
                          hasEvaluated 
                            ? 'bg-gradient-to-br from-green-500 to-emerald-500' 
                            : 'bg-gradient-to-br from-amber-500 to-orange-500'
                        }`}>
                          {hasEvaluated ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <>{member.first_name?.[0]}{member.last_name?.[0]}</>
                          )}
                        </div>
                        {member.first_name} {member.last_name}
                        {hasEvaluated && (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                      </span>
                    );
                  })}
                </div>
                {/* Summary */}
                {(() => {
                  const evaluatedCount = scheduledTeam.filter(m => 
                    test.evaluations?.some(e => e.evaluator_id === m.id)
                  ).length;
                  return (
                    <p className="text-xs text-amber-600 mt-3">
                      Ohodnoceno: {evaluatedCount} z {scheduledTeam.length} hodnotitelů
                    </p>
                  );
                })()}
              </div>
            )}

            {/* Photo Gallery */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-purple-500" />
                Fotografie ({test.attachments?.length || 0})
              </h2>
              
              {/* Upload area */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all mb-4 ${
                  isDragActive 
                    ? 'border-purple-400 bg-purple-50' 
                    : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragActive ? 'text-purple-500' : 'text-slate-400'}`} />
                {uploading ? (
                  <p className="text-sm text-purple-600">Nahrávám...</p>
                ) : isDragActive ? (
                  <p className="text-sm text-purple-600">Pusťte pro nahrání</p>
                ) : (
                  <p className="text-sm text-slate-500">
                    Přetáhněte fotky sem nebo klikněte pro výběr
                  </p>
                )}
              </div>
              
              {/* Photo grid */}
              {test.attachments && test.attachments.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {test.attachments.map((attachment, index) => (
                    <div 
                      key={attachment.file_id || index}
                      className="relative group aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200"
                    >
                      <img
                        src={`data:${attachment.content_type};base64,${attachment.data}`}
                        alt={attachment.filename}
                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => openLightbox(attachment)}
                      />
                      {/* Overlay with actions - pointer-events-none except for buttons */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 pointer-events-none">
                        <button
                          onClick={(e) => { e.stopPropagation(); openLightbox(attachment); }}
                          className="p-2 bg-white rounded-full shadow-lg hover:bg-slate-100 pointer-events-auto"
                          title="Náhled"
                        >
                          <ImageIcon className="h-4 w-4 text-slate-700" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadPhoto(attachment); }}
                          className="p-2 bg-white rounded-full shadow-lg hover:bg-slate-100 pointer-events-auto"
                          title="Stáhnout"
                        >
                          <Download className="h-4 w-4 text-slate-700" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeletePhoto(attachment.file_id); }}
                          className="p-2 bg-white rounded-full shadow-lg hover:bg-red-100 pointer-events-auto"
                          title="Smazat"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Žádné fotografie</p>
              )}
            </div>

            {/* Linked Reports */}
            {linkedReports.length > 0 && (
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-200 p-6">
                <h2 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Vytvořená hlášení ({linkedReports.length})
                </h2>
                <div className="space-y-2">
                  {linkedReports.map((report) => (
                    <Link
                      key={report.id}
                      to={`/reports/${report.id}`}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-teal-100 hover:border-teal-300 hover:shadow-sm transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: report.status_color }}
                        />
                        <div>
                          <div className="font-medium text-slate-900 group-hover:text-teal-700">
                            {report.title}
                          </div>
                          <div className="text-xs text-slate-500">
                            {report.status_name} • {formatDate(report.created_at)}
                          </div>
                        </div>
                      </div>
                      <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-teal-600" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-900 mb-4">Souhrn hodnocení</h2>
              
              {test.evaluations?.length > 0 ? (
                <div className="space-y-4">
                  {evalFields.map((field) => {
                    const summary = test.summary?.[field.name] || { A: 0, B: 0, C: 0 };
                    const total = summary.A + summary.B + summary.C;
                    
                    return (
                      <div key={field.id} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-700">{field.name}</span>
                          <span className="text-xs text-slate-400">{total} hodnocení</span>
                        </div>
                        <div className="flex gap-1 h-3 rounded-full overflow-hidden bg-slate-100">
                          {summary.A > 0 && (
                            <div 
                              className="bg-green-500 transition-all" 
                              style={{ width: `${(summary.A / total) * 100}%` }}
                              title={`A: ${summary.A}`}
                            />
                          )}
                          {summary.B > 0 && (
                            <div 
                              className="bg-amber-500 transition-all" 
                              style={{ width: `${(summary.B / total) * 100}%` }}
                              title={`B: ${summary.B}`}
                            />
                          )}
                          {summary.C > 0 && (
                            <div 
                              className="bg-red-500 transition-all" 
                              style={{ width: `${(summary.C / total) * 100}%` }}
                              title={`C: ${summary.C}`}
                            />
                          )}
                        </div>
                        <div className="flex gap-3 text-xs">
                          <span className="text-green-600">A: {summary.A}</span>
                          <span className="text-amber-600">B: {summary.B}</span>
                          <span className="text-red-600">C: {summary.C}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-400 text-sm text-center py-4">
                  Zatím žádné hodnocení
                </p>
              )}
            </div>
          </div>

          {/* Right column - Evaluations list */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-900 mb-4">
                Hodnocení ({test.evaluations?.length || 0})
              </h2>
              
              {test.evaluations?.length > 0 ? (
                <>
                  {/* Split evaluations by group for Krabicky */}
                  {sensoryType === 'krabicky' ? (
                    <>
                      {/* Group A evaluations */}
                      {(() => {
                        const groupAEvals = test.evaluations.filter(e => {
                          const member = teamMembers.find(m => m.id === e.evaluator_id);
                          return member?.is_group_a === true;
                        });
                        const groupBEvals = test.evaluations.filter(e => {
                          const member = teamMembers.find(m => m.id === e.evaluator_id);
                          return !member?.is_group_a;
                        });
                        
                        return (
                          <div className="space-y-6">
                            {/* Group A Section */}
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                                <h3 className="font-semibold text-purple-800">Skupina A - rozšířené hodnocení ({groupAEvals.length})</h3>
                              </div>
                              {groupAEvals.length > 0 ? (
                                <div className="space-y-4 pl-5 border-l-2 border-purple-200">
                                  {groupAEvals.map((evaluation) => (
                                    <EvaluationCard 
                                      key={evaluation.id} 
                                      evaluation={evaluation} 
                                      onDelete={handleDeleteEvaluation}
                                      groupBadge={<Badge className="bg-purple-500 text-white text-xs">Skupina A</Badge>}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-slate-400 italic pl-5">Zatím žádné hodnocení od skupiny A</p>
                              )}
                            </div>
                            
                            {/* Group B Section */}
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                                <h3 className="font-semibold text-teal-800">Skupina B - základní hodnocení ({groupBEvals.length})</h3>
                              </div>
                              {groupBEvals.length > 0 ? (
                                <div className="space-y-4 pl-5 border-l-2 border-teal-200">
                                  {groupBEvals.map((evaluation) => (
                                    <EvaluationCard 
                                      key={evaluation.id} 
                                      evaluation={evaluation} 
                                      onDelete={handleDeleteEvaluation}
                                      groupBadge={<Badge className="bg-teal-500 text-white text-xs">Skupina B</Badge>}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-slate-400 italic pl-5">Zatím žádné hodnocení od skupiny B</p>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    /* Hotovky - show all evaluations together without group distinction */
                    <div className="space-y-4">
                      {test.evaluations.map((evaluation) => (
                        <EvaluationCard 
                          key={evaluation.id} 
                          evaluation={evaluation} 
                          onDelete={handleDeleteEvaluation}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <FlaskConical className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 mb-4">Zatím žádné hodnocení</p>
                  <Button 
                    onClick={() => setDialogOpen(true)}
                    className="bg-gradient-to-r from-purple-500 to-pink-500"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Přidat první hodnocení
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upravit zkoušku</DialogTitle>
            <DialogDescription>
              Upravte údaje senzorické zkoušky
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Den zkoušky</Label>
              <DatePicker
                value={editData.test_date}
                onChange={(dateStr) => setEditData(prev => ({ ...prev, test_date: dateStr || '' }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Název jídla</Label>
              <Input
                value={editData.food_name || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, food_name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Šarže</Label>
              <Input
                value={editData.batch || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, batch: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Datum výroby</Label>
              <DatePicker
                value={editData.production_date}
                onChange={(dateStr) => setEditData(prev => ({ ...prev, production_date: dateStr || '' }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>DMT (datum spotřeby)</Label>
              <DatePicker
                value={editData.dmt}
                onChange={(dateStr) => setEditData(prev => ({ ...prev, dmt: dateStr || '' }))}
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Zrušit
              </Button>
              <Button 
                onClick={handleSaveEdit}
                disabled={submitting}
                className="bg-gradient-to-r from-purple-500 to-pink-500"
              >
                {submitting ? 'Ukládám...' : 'Uložit změny'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox for photo preview */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black/95">
          <div className="relative">
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            {lightboxImage && (
              <>
                <img
                  src={`data:${lightboxImage.content_type};base64,${lightboxImage.data}`}
                  alt={lightboxImage.filename}
                  className="max-h-[80vh] w-full object-contain"
                />
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="flex items-center justify-between">
                    <div className="text-white">
                      <p className="font-medium">{lightboxImage.filename}</p>
                      <p className="text-sm text-white/70">
                        {(lightboxImage.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDownloadPhoto(lightboxImage)}
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Stáhnout
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SensorikaDetailPage;
